import type { ChangeEvent, ComponentProps, MouseEvent } from 'react';
import { cn } from '../../common/functions/cn';

export interface SwitchProps extends Omit<ComponentProps<'input'>, 'type' | 'role' | 'size'> {
  size?: 'sm' | 'default';
  /** Fires with the new checked state on every user-driven change. */
  onCheckedChange?: (checked: boolean) => void;
}

const Switch = ({
  className,
  size = 'default',
  onCheckedChange,
  onChange,
  onClick,
  readOnly,
  disabled,
  ...props
}: SwitchProps) => {
  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (readOnly) return;
    onChange?.(event);
    onCheckedChange?.(event.target.checked);
  };

  // HTML `readonly` doesn't actually prevent checkbox toggling. Block the click so the input
  // doesn't change state in uncontrolled mode (controlled mode is also covered as a side effect).
  // Keeps the input focusable, unlike `disabled`. Space also fires a click, so this covers keyboard.
  const handleClick = (event: MouseEvent<HTMLInputElement>) => {
    if (readOnly) {
      event.preventDefault();
    }
    onClick?.(event);
  };

  return (
    <input
      type="checkbox"
      // biome-ignore lint/a11y/useAriaPropsForRole: the native checked state maps to the switch role's checked state (HTML-AAM). ARIA in HTML forbids aria-checked on input[type=checkbox]
      role="switch"
      data-size={size}
      readOnly={readOnly}
      disabled={disabled}
      onChange={handleChange}
      onClick={handleClick}
      className={cn(
        'peer relative inline-flex shrink-0 cursor-pointer appearance-none items-center rounded-full border border-transparent outline-none transition-all focus-visible:outline-hidden',
        'before:absolute before:-inset-x-3 before:-inset-y-2',
        'focus-visible:border-ring focus-visible:ring-2 focus-visible:ring-ring/50',
        'aria-invalid:border-error aria-invalid:ring-2 aria-invalid:ring-error/20',
        'aria-invalid:focus-visible:border-error aria-invalid:focus-visible:ring-error/60',
        'data-[size=default]:h-[18.4px] data-[size=sm]:h-[14px] data-[size=default]:w-[32px] data-[size=sm]:w-[24px]',
        'dark:aria-invalid:border-error/50 dark:aria-invalid:ring-error/40',
        'not-checked:bg-border checked:bg-primary',
        'disabled:cursor-not-allowed disabled:opacity-50',
        'after:block after:rounded-full after:bg-background after:transition-transform',
        'data-[size=default]:after:size-4 data-[size=sm]:after:size-3',
        'checked:after:translate-x-[calc(100%-2px)]',
        'dark:not-checked:after:bg-foreground dark:checked:after:bg-primary-foreground',
        readOnly && 'cursor-not-allowed',
        className
      )}
      {...props}
    />
  );
};

export { Switch };
